import {
    FinancePaymentViewModel,
    RenderedServiceData,
    RenderedServiceWithEventData,
} from 'chaika-frontend-types/frontend-cms';
import { RenderedServiceEventName } from 'chaika-enums';

import { DateHelper } from '@utils/DateHelper';
import { store } from '@store';

enum EventType {
    Cancel = 'cancel',
    PriceChange = 'price_change',
}

export interface FormattedRenderedService {
    rendered_service_id?: number;
    patient_id?: number;
    lastname?: string;
    firstname?: string;
    patronym?: string;
    date?: string;
    name?: string;
    price?: number;
    quantity?: number;
    sum?: number;
    store_uuid?: string;
}

export interface FormattedPayments {
    id?: string;
    patient_id?: number;
    lastname?: string;
    firstname?: string;
    patronym?: string;
    date?: string;
    type?: string;
    amount?: number;
    rendered_service_ids?: number[];
    store_uuid?: string;
}

export interface FormattedRenderedServicesChanges {
    type?: EventType;
    amount?: string;
    rendered_service_id?: number;
    date?: string;
}

export function useFormatData() {
    const { organizationList, patientStore, serviceStore } = store;

    function formatRenderedServices(renderedServices: RenderedServiceData[]): FormattedRenderedService[] {
        return renderedServices.map(item => {
            const ehr = patientStore.patientList.getExisting(item.ehrId);
            const service = serviceStore.serviceList.getExisting(item.serviceId);
            return {
                rendered_service_id: item.externalId,
                patient_id: ehr.recordId,
                lastname: ehr.firstName,
                firstname: ehr.lastName,
                patronym: ehr.patronymic,
                date: DateHelper.toSimpleDate(item.creationTime),
                name: service.name,
                price: item.price,
                quantity: 1,
                sum: item.amountToPay,
                store_uuid: organizationList.getExisting(item.executorOrganizationId ?? item.organizationId).storeId,
            };
        });
    }

    function formatPayments(
        payments: FinancePaymentViewModel[],
        externalIdByRenderedServiceId: Map<string, number>,
    ): FormattedPayments[] {
        return payments
            .filter(payment => payment.renderedServiceIds.length > 0)
            .map(payment => {
                const ehr = patientStore.patientList.getExisting(payment.patientId);
                return {
                    id: payment.id,
                    patient_id: ehr.recordId,
                    lastname: ehr.lastName,
                    firstname: ehr.firstName,
                    patronym: ehr.patronymic,
                    date: DateHelper.toSimpleDate(payment.createdAt),
                    type: payment.method.type,
                    amount: payment.amount.value,
                    rendered_service_ids: payment.renderedServiceIds
                        .map(id => externalIdByRenderedServiceId.get(id))
                        .filter((externalId): externalId is number => externalId !== undefined),
                    store_uuid: organizationList.getExisting(payment.organizationId).storeId,
                };
            });
    }

    function formatRenderedServicesChanges(
        renderedServices: RenderedServiceWithEventData[],
    ): FormattedRenderedServicesChanges[] {
        const changes: FormattedRenderedServicesChanges[] = [];
        renderedServices.forEach(service => {
            service.events
                .filter(serviceEvent => !!getEventType(serviceEvent.name))
                .forEach(serviceEvent => {
                    const type = getEventType(serviceEvent.name);
                    const amount = type === EventType.PriceChange ? serviceEvent.body.amountToPay : undefined;

                    changes.push({
                        type,
                        amount,
                        rendered_service_id: service.externalId,
                        date: DateHelper.toSimpleDate(serviceEvent.occurredOn),
                    });
                });
        });
        return changes;
    }

    function getEventType(eventName: string) {
        let type;
        switch (eventName) {
            case RenderedServiceEventName.RenderedServiceCanceled:
            case RenderedServiceEventName.RenderedServiceDeleted: {
                type = EventType.Cancel;
                break;
            }
            case RenderedServiceEventName.RenderedServicePaymentChanged:
            case RenderedServiceEventName.RenderedServicePlanAdded:
            case RenderedServiceEventName.RenderedServicePlanChanged:
            case RenderedServiceEventName.RenderedServicePlanRemoved:
            case RenderedServiceEventName.RenderedServiceUpdated: {
                type = EventType.PriceChange;
                break;
            }
        }

        return type;
    }

    return {
        formatPayments,
        formatRenderedServices,
        formatRenderedServicesChanges,
    };
}
